import { Subject, of, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService, CommunicationConfigService, IsSigninService, CsrfService } from '@argalis-apps/angular-global';
import { AuthConfigService } from '../auth-config/auth-config.service';
import { MUser } from '@argalis-apps/models';
import { AuthRouting } from '../../auth-routing';
import { Router } from '@angular/router';

const TESTDATA = {
  login: 'test.email@test.tst',
  type: MUser.ETypeUser.CLIENT,
  userInfo: new MUser.CContact({
    id: 1,
    civ: 'M.',
    name: 'Trucmuche',
    firstName: 'Jean Michel',
    role: 'Testeur',
    mail: 'test.email@test.tst',
    phone: '0411223344',
    mobile: '0123456789',
    birthDate: null,
    addr: new MUser.CAddress({
      addr: '2A rue du test',
      postal: '42042',
      city: 'Testville'
    })
  }),
  company: new MUser.CClient(),
  access: []
};
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _userConnected: MUser.CUser = null;
  public get userConnected(): MUser.CUser {
    return this._userConnected;
  }
  public userChange = new Subject<MUser.CUser>();

  constructor(
    private authConfigService: AuthConfigService,
    private communicationConfigService: CommunicationConfigService,
    private http: HttpClient,
    private configService: ConfigService,
    private isSigninService: IsSigninService,
    private router: Router,
    private csrfService: CsrfService
  ) {
    // get user from ? cookie, localStorage csrf, fucking cache, ...
    this.userChange.next(this._userConnected);
  }

  getRoute(identifier: string): string[] {
    // search for complete route corresponding to identifier
    const routeFound = AuthRouting.find(route => route.path === identifier);
    if (routeFound) return ['/', this.authConfigService.routingBaseUri, routeFound.path];
    // fallback to module home route if exists
    const baseRoute = AuthRouting.find(route => route.path === '');
    if (baseRoute) return ['/', this.authConfigService.routingBaseUri];
    // fallback to home route (TBD: error page ?)
    return ['/'];
  }

  userChangeNext(user: MUser.CUser) {
    this._userConnected = new MUser.CUser(user);
    this.userChange.next(this._userConnected);
  }

  signin(email: string, password: string) {
    if (this.configService.getOptions().useTestData === true) {
      this._userConnected = new MUser.CUser(TESTDATA);
      this.userChange.next(this._userConnected);
      return of('OK'); // retour d'un csrf de connexion ?
    } else {
      const eTypeUser: MUser.ETypeUser = this.configService.getETypeUser();
      return this.http.post(`${this.communicationConfigService.getApiUri()}/sign/in`, {
        email,
        password,
        extranet: true,
        eTypeUser
      });
    }
  }

  /**
   * gère la reconnexion à l'app si l'utilisateur est déjà connecté
   * @returns {Promise<boolean>}: retourne true si connecté et pas de problème. false si problème
   */
  async resignin(): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (this.configService.getOptions().useTestData === true) {
        resolve(true);
      } else {
        this.isSigninService.checkSignIn().subscribe(
          (res: any) => {
            if (res) {
              // Si contrôle OK, on identifie l'utilisateur
              if (!this.configService.getOptions().useTestData && res.user) {
                this.userChangeNext(res.user);
              }
              if (res.csrf) this.csrfService.setToken(res.csrf);
              resolve(true);
            } else {
              // Si contrôle KO, on efface seulement le localStorage, le guardian se charge de la redirection
              this.csrfService.removeToken();
              resolve(false);
            }
          },
          () => {
            resolve(false);
          }
        );
      }
    });
  }

  signout() {
    if (this.configService.getOptions().useTestData === true) {
      this._userConnected = null;
      this.userChange.next(this._userConnected);
      return of('BYE'); // idée: peut-être effacer le csrf ?
    } else {
      return this.http.post(`${this.communicationConfigService.getApiUri()}/sign/out/`, {
        extranet: true
      });
    }
  }

  signoutAndNavigate(landingRoute: string[] = ['']) {
    return new Promise<void>(resolve => {
      this.signout().subscribe(
        () => {
          this.userChangeNext(null);
          this.csrfService.removeToken();
          setTimeout(() => {
            if (landingRoute && landingRoute.length > 0) this.router.navigate(landingRoute);
          });
          resolve();
        },
        err => {
          console.log(`${new Date().toISOString()} NOPE :`, err);
          resolve();
        }
      );
    });
  }

  renewPassword(email: string) {
    if (this.configService.getOptions().useTestData === true) {
      return of('OK');
    } else {
      let baseRoute = '';
      switch (this.configService.getETypeUser()) {
        case MUser.ETypeUser.FORMATEUR:
          baseRoute = 'intervenant';
          break;
        case MUser.ETypeUser.APPRENANT:
          baseRoute = 'apprenant';
          break;
        case MUser.ETypeUser.CLIENT:
          baseRoute = 'client';
          break;
      }
      return this.http.post(`${this.communicationConfigService.getApiUri()}/${baseRoute}/renewpassword`, {
        email,
        extranet: true
      });
    }
  }

  changePassword(idUser, password, b_ost: boolean): Observable<{ success: boolean }> {
    if (this.configService.getOptions().useTestData) {
      return of({ success: true });
    } else {
      const reqBody = {
        id: idUser,
        eTypeUser: this.configService.getETypeUser(),
        password: password,
        b_ost: b_ost
      };
      return this.http.post<{ success: boolean }>(
        `${this.communicationConfigService.getApiUri()}/sign/change-password`,
        reqBody
      );
    }
  }
}
